import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ContentCutRoundedIcon from '@mui/icons-material/ContentCutRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Typography
} from '@mui/material';
import React from 'react';
import OrderPaymentChip from './OrderPaymentChip';
import PaymentStatusControl from './PaymentStatusControl';
import OrderStatusSelect from './OrderStatusSelect';
import {
  formatOrderDate,
  formatOrderMoney,
  getAddressLines,
  getCustomerName,
  getOrderNumber
} from './orderUtils';

export default function OrderRow({
  order,
  onOpen,
  onStatusChange,
  isStatusSaving,
  onDownloadReceipt,
  isReceiptDownloading,
  onDownloadProductionNotes,
  isProductionNotesDownloading,
  onPaymentStatusChange,
  isPaymentSaving,
  onDelete,
  isSelected,
  onToggleSelected
}) {
  const address = getAddressLines(order);

  return (
    <TableRow
      hover
      selected={isSelected}
      sx={{ '&:last-child td': { borderBottom: 0 }, userSelect: 'text' }}
    >
      <TableCell padding="checkbox">
        <IconButton
          aria-label={`Отвори поръчка ${getOrderNumber(order)}`}
          onClick={() => onOpen(order.id)}
          title="Отвори поръчката"
        >
          <ArrowForwardRoundedIcon fontSize="small" />
        </IconButton>
      </TableCell>
      <TableCell
        padding="checkbox"
        sx={{
          bgcolor: 'background.paper',
          left: 0,
          minWidth: 56,
          position: 'sticky',
          zIndex: 3,
          'tr:hover &': { bgcolor: 'action.hover' }
        }}
      >
        <Checkbox
          checked={isSelected}
          inputProps={{ 'aria-label': `Избери поръчка ${getOrderNumber(order)}` }}
          onClick={(event) => event.stopPropagation()}
          onChange={() => onToggleSelected(order.id)}
          onKeyDown={(event) => event.stopPropagation()}
        />
      </TableCell>
      <TableCell>
        <Typography fontWeight={750}>#{getOrderNumber(order)}</Typography>
        <Typography variant="caption" color="text.secondary">
          {formatOrderDate(order.createdAt)}
        </Typography>
      </TableCell>
      <TableCell sx={{ minWidth: 220 }}>
        <OrderStatusSelect
          value={order.status}
          onChange={(status) => onStatusChange(order, status)}
          disabled={isStatusSaving}
          label={`Статус на поръчка ${getOrderNumber(order)}`}
        />
      </TableCell>
      <TableCell
        sx={{
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          boxShadow: '8px 0 10px -10px rgba(15, 23, 42, 0.55)',
          left: 56,
          minWidth: 240,
          position: 'sticky',
          isolation: 'isolate',
          zIndex: 2
        }}
      >
        <Typography fontWeight={650}>{getCustomerName(order)}</Typography>
        <Typography variant="body2" color="text.secondary">
          {order.email || 'Няма имейл'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {order.phone || 'Няма телефон'}
        </Typography>
      </TableCell>
      <TableCell sx={{ minWidth: 190 }}>
        {address.length ? (
          address.map((line, index) => (
            <Typography variant="body2" key={`${line}-${index}`}>
              {line}
            </Typography>
          ))
        ) : (
          <Typography variant="body2">—</Typography>
        )}
        {order.shippingType && (
          <Typography variant="caption" color="text.secondary">
            {order.shippingType}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ minWidth: 175 }}>
        <Stack spacing={0.75} alignItems="flex-start">
          <OrderPaymentChip paymentType={order.paymentType} bankPayment={order.isBankPayment} />
          {order.isBankPayment && (
            <PaymentStatusControl
              paymentStatus={order.paymentStatus}
              onChange={(paymentStatus) => onPaymentStatusChange(order, paymentStatus)}
              saving={isPaymentSaving}
            />
          )}
        </Stack>
      </TableCell>
      <TableCell sx={{ minWidth: 205 }}>
        <Stack spacing={0.75}>
          <Stack direction="row" justifyContent="space-between" gap={2}>
            <Typography variant="caption" color="text.secondary">
              Междинна сума
            </Typography>
            <Typography variant="body2" fontWeight={800}>
              {formatOrderMoney(order.subtotal, order.currency)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" gap={2}>
            <Typography variant="caption" color="text.secondary">
              Доставка
            </Typography>
            <Typography variant="body2">
              {formatOrderMoney(order.shippingPrice, order.currency)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" gap={2}>
            <Typography variant="caption" color="text.secondary">
              Общо
            </Typography>
            <Typography variant="body2">{formatOrderMoney(order.total, order.currency)}</Typography>
          </Stack>
        </Stack>
      </TableCell>
      <TableCell sx={{ minWidth: 125 }}>
        {order.editedAt ? (
          <Stack spacing={0.5} alignItems="flex-start">
            <Chip size="small" color="secondary" label="Редактирана" />
            <Typography variant="caption" color="text.secondary">
              {formatOrderDate(order.editedAt)}
            </Typography>
          </Stack>
        ) : (
          <Typography color="text.secondary">—</Typography>
        )}
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.5}>
          <Button
            aria-label={`Дигитална бележка за поръчка ${getOrderNumber(order)}`}
            disabled={isReceiptDownloading}
            onClick={(event) => {
              event.stopPropagation();
              onDownloadReceipt(order);
            }}
            onKeyDown={(event) => event.stopPropagation()}
            size="small"
            startIcon={
              isReceiptDownloading ? <CircularProgress size={16} /> : <DownloadRoundedIcon fontSize="small" />
            }
            variant="outlined"
          >
            Дигитална бележка
          </Button>
          <Button
            aria-label={`Бележки за производство за поръчка ${getOrderNumber(order)}`}
            disabled={isProductionNotesDownloading}
            onClick={(event) => {
              event.stopPropagation();
              onDownloadProductionNotes(order);
            }}
            onKeyDown={(event) => event.stopPropagation()}
            size="small"
            startIcon={
              isProductionNotesDownloading ? (
                <CircularProgress size={16} />
              ) : (
                <ContentCutRoundedIcon fontSize="small" />
              )
            }
            variant="outlined"
          >
            Бележки за производство
          </Button>
          <IconButton
            aria-label={`Изтрий поръчка ${getOrderNumber(order)}`}
            color="error"
            onClick={(event) => {
              event.stopPropagation();
              onDelete(order);
            }}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <DeleteOutlineRoundedIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label={`Отвори поръчка ${getOrderNumber(order)} отдясно`}
            onClick={() => onOpen(order.id)}
            title="Отвори поръчката"
          >
            <ArrowForwardRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  );
}
